import { useSelector } from 'react-redux'
import SeedClassSelectors from 'modules/domain/seedClass/selectors'
import { Progress, ResourceHook } from 'modules/types'
import SeedClassActions from 'modules/domain/seedClass/duck'
import { SeedClass, SeedClassListRequestFilter } from 'modules/domain/seedClass/types'
import { useEffect, useState } from 'react'
import { useAction, useDidMount, usePageQuery } from '@agro-club/frontend-shared'
import * as managers from 'modules/domain/seedClass/managers'
import { makeCancelable } from 'modules/utils/httpClient'

export const useSeedClassList: ResourceHook<SeedClass[], void[]> = () => {
  const progress = useSelector(SeedClassSelectors.listFetchProgress)
  const list = useSelector(SeedClassSelectors.seedClassList)
  const page = usePageQuery()
  const fetchAction = useAction(SeedClassActions.listRequested, page ? { page } : {})

  useDidMount(() => {
    if (progress !== Progress.SUCCESS) {
      fetchAction()
    }
  })

  return [progress, list]
}

export const useSeedClassListWithFilter = (filter: SeedClassListRequestFilter) => {
  const [progress, setProgress] = useState<Progress>(Progress.IDLE)
  const [list, setList] = useState<SeedClass[]>([])
  const cropId = filter.crop_id

  useEffect(() => {
    if (!cropId) {
      setList([])
      setProgress(Progress.IDLE)
      return
    }

    setProgress(Progress.WORK)
    const cancelableFn = makeCancelable(managers.getSeedClassesList)
    const [promise, cancel] = cancelableFn({ crop_id: cropId }, {}, 1, 1000)

    promise
      .then((response) => {
        setList(response.data || [])
        setProgress(Progress.SUCCESS)
      })
      .catch((err: any) => {
        if (!err?.isCanceled) {
          setProgress(Progress.ERROR)
        }
      })

    return () => {
      cancel()
    }
  }, [cropId])

  return [progress, list] as const
}

export const useSeedClass: ResourceHook<SeedClass, [string]> = (id: string) => {
  const meta = useSelector((state) => SeedClassSelectors.meta(state, id))
  const seedClass = useSelector((state) => SeedClassSelectors.seedClass(state, id))
  const fetchAction = useAction(SeedClassActions.itemRequested, id)

  useDidMount(() => {
    if (!seedClass || seedClass.id !== id) {
      fetchAction()
    }
  })

  return [meta.fetchProgress, seedClass]
}

export const useSeedClassById: ResourceHook<SeedClass, [string]> = (id: string) => {
  const meta = useSelector((state) => SeedClassSelectors.meta(state, id))
  const item = useSelector((state) => SeedClassSelectors.seedClass(state, id))
  const fetchAction = useAction(SeedClassActions.itemRequested, id)

  useEffect(() => {
    if (!item || id !== item.id) {
      fetchAction()
    }
  }, [item, fetchAction, id])

  return [meta.fetchProgress, item]
}
